import React, { useEffect, useState } from "react";
import DataTableBase from "../../Components/Common/DataTableBase/DataTableBase";
import { useTranslation } from "react-i18next";
import { useMenuStore } from "../../stores/Assets/menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
} from "reactstrap";
import AddMenu from "./Components/AddMenu";
import EditMenu from "./Components/EditMenu";
import { Link, useLocation } from "react-router-dom";
import { useConfirmDialogStore } from "../../stores/Modal/ConfirmDialogStore";
import { QRCode } from "react-qrcode-logo";
import { Pagination } from "../../Components/Common/DataTableBase/Pagination";
import { useSiteStore } from "../../stores/Assets/site";
import { useKitchenStore } from "../../stores/Assets/kitchen";
import { canEdit } from "../../helpers/permissions";
import SearchBar from "../../Components/Common/SearchBar";
import Skeleton from "../../Components/Common/Skeleton";
import { useUsageStore } from "../../stores/Usage";
import { PlanUpgradeModal } from "../../Components/Common/PlanUpgradeModal";
import MenuOrderingModal from "./Components/MenuOrderingModal";
import BulkAddMenusModal from "./Components/BulkAddMenusModal";

const Menu = () => {
  const { t } = useTranslation();
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  document.title = t("Clickorder Admin");
  const location = useLocation();
  const menuId = new URLSearchParams(location.search).get("menuId");
  const { getMenus, isLoading, menus, deleteMenu, pagination } = useMenuStore(
    (state) => state
  );
  const { fetchUsage, getLimitInfo } = useUsageStore();
  const menuLimit = getLimitInfo("menus");
  const isLimitReached = menuLimit?.isReached;
  const { getSites } = useSiteStore((state) => state);
  const { getKitchens, kitchens } = useKitchenStore((state) => state);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("All");
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [showSiteError, setShowSiteError] = useState(false);
  const [showKitchenError, setShowKitchenError] = useState(false);

  useEffect(() => {
    const fetchSites = async () => {
      setLoading(true);
      const siteList = await getSites();
      setSites(siteList);
      setLoading(false);
    };
    fetchSites();
  }, [getSites]);
  useEffect(() => {
    fetchUsage();
    getKitchens({ page: 1, perPage: 100 });
  }, []);

  useEffect(() => {
    setTotalRows(pagination?.totalItems || 0);
  }, [pagination]);
  const columns = [
    {
      name: t("ID"),
      width: "70px",
      selector: (row) => row?.menu_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.menu_id}</span>
        </div>
      ),
    },
    {
      name: t("Site"),
      // width: "100px",
      selector: (row) => row?.sites.length > 0 && row?.sites[0]?.site_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.sites.length > 0 &&
              `${row?.sites[0]?.name} (id: ${row?.sites[0]?.site_id}) `}
          </span>
        </div>
      ),
    },
    {
      name: t("Kitchen"),
      // width: "100px",
      selector: (row) => row?.kitchen?.kitchen_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.kitchen_id &&
              `${row?.kitchen?.name} (id: ${row?.kitchen?.kitchen_id}) `}
          </span>
        </div>
      ),
    },
    {
      name: t("Name"),
      // width: "100px",
      selector: (row) => row?.name,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.name}</span>
        </div>
      ),
    },
    {
      name: t("Name (AR)"),
      // width: "100px",
      selector: (row) => row?.name_ar,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.name_ar}</span>
        </div>
      ),
    },
    {
      name: t("Alias"),
      selector: (row) => row?.alias,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.alias}</span>
        </div>
      ),
    },

    // {
    //   name: t("Ask"),
    //   selector: (row) => row?.ask,
    //   sortable: true,
    //   wrap: true,
    //   cell: (row) => {
    //     const truncatedAsk = row?.ask
    //       ? row?.ask?.slice(0, 25) + (row?.ask?.length > 25 ? "..." : "")
    //       : ""; // Truncate to first 5 characters with ellipsis if longer

    //     return (
    //       <div
    //         className="d-flex flex-row justify-content-center align-items-center gap-2"
    //         // id={`anchor-${row?.ask}`} // Unique ID for the tooltip
    //       >
    //         <span>{truncatedAsk}</span>
    //         {/* <UncontrolledTooltip placement="top" target={`anchor-${row?.ask}`}>
    //           {row?.ask}
    //         </UncontrolledTooltip> */}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   name: t("Ask (AR)"),
    //   // width: "100px",
    //   selector: (row) => row?.ask_ar,
    //   sortable: true,
    //   wrap: true,
    // },
    {
      name: t("Currency"),
      // width: "100px",
      selector: (row) => row?.currency,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.currency} {row?.currency_ar && `(${row?.currency_ar})`}
          </span>
        </div>
      ),
    },

    {
      name: t("VAT"),
      width: "80px",
      selector: (row) => row?.VAT,
      sortable: true,
      wrap: true,
    },

    {
      name: t("Items"),
      // width: "95px",
      // selector: (row) => row?.status,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <a
          href={`/menu-items/${row?.menu_id}`}
          className="cursor-pointer"
          id={`anchor-${row?.menu_id}`}
        >
          <span
            style={{ fontSize: "14px" }}
            className={`badge bg-soft-info cursor-pointer text-success`}
          >
            {row?.items_count ?? 0} Items
          </span>
          <UncontrolledTooltip
            placement="top"
            target={`anchor-${row?.menu_id}`}
          >
            {" "}
            View menu items
          </UncontrolledTooltip>
        </a>
      ),
    },
  ];

  const searchHandler = (searchText) => {
    setSearchTerm(searchText);
    setCurrentPage(1);
  };
  const { showConfirm } = useConfirmDialogStore((state) => state);

  const [showAddMenuModal, setShowAddMenuModal] = useState(false);
  const [showEditMenuModal, setShowEditMenuModal] = useState(false);
  const [showOrderingModal, setShowOrderingModal] = useState(false);
  const [showBulkAddMenusModal, setShowBulkAddMenusModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const toggleUpgradeModal = () => setShowUpgradeModal(!showUpgradeModal);

  const toggleAddMenuModal = () => {
    if (!showAddMenuModal && isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    if (sites.length === 0) {
      setShowSiteError(true);
      return;
    }
    if (kitchens.length === 0) {
      setShowKitchenError(true);
      return;
    }
    setShowAddMenuModal(!showAddMenuModal);
  };

  const toggleBulkAddMenusModal = () => {
    if (!showBulkAddMenusModal && isLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    if (sites.length === 0) {
      setShowSiteError(true);
      return;
    }
    if (kitchens.length === 0) {
      setShowKitchenError(true);
      return;
    }
    setShowBulkAddMenusModal(!showBulkAddMenusModal);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const toggleEditMenuModal = (row) => {
    setSelectedRow(row);
    setShowEditMenuModal(!showEditMenuModal);
  };

  const onChangePage = (page) => {
    const siteIdToUse = selectedSiteId === "All" ? null : parseInt(selectedSiteId);
    getMenus(siteIdToUse, page, rowsPerPage, searchTerm);
  };

  useEffect(() => {
    const siteIdToUse = selectedSiteId === "All" ? null : parseInt(selectedSiteId);
    getMenus(siteIdToUse, 1, rowsPerPage, searchTerm);
  }, [selectedSiteId, searchTerm]);
  const deleteMenuFun = async (id) => {
    try {
      await deleteMenu(id);
      fetchUsage();
    } catch (e) {
      console.log(e);
    }
  };

  const handleSiteSelect = (e) => {
    const id = e.target.value;
    setSelectedSiteId(id);
  };

  const [currentMenus, setCurrentMenus] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const rowsPerPage = 10; // Display 5 rows per page

  const onPageChange = (page) => {
    setCurrentPage(page);
    getMenus(
      selectedSiteId === "All" ? null : parseInt(selectedSiteId),
      page,
      rowsPerPage
    );
  };

  const [canEditMenu, setCanEditMenu] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("menu");
      setCanEditMenu(res);
    };

    checkPermission();
  }, []);
  return (
    <>
      {showSiteError && (
        <Modal isOpen={showSiteError} toggle={() => setShowSiteError(false)}>
          <ModalHeader
            className={"flex align-items-center justify-content-center"}
          >
            <h4 className="m-0">{t("No Registered Site")}</h4>
          </ModalHeader>
          <ModalBody>
            <div className="text-center ">
              <p className="text-danger">{t("You should first add a site.")}</p>
              <Link to="/sites" className="btn btn-primary mr-5">
                {t("Add Site")}
              </Link>
            </div>
          </ModalBody>
        </Modal>
      )}
      {showKitchenError && (
        <Modal
          isOpen={showKitchenError}
          toggle={() => setShowKitchenError(false)}
        >
          <ModalHeader
            className={"flex align-items-center justify-content-center"}
          >
            <h4 className="m-0">{t("No Registered Kitchen")}</h4>
          </ModalHeader>
          <ModalBody>
            <div className="text-center ">
              <p className="text-danger">
                {t("You should first add a kitchen.")}
              </p>
              <Link to="/kitchens" className="btn btn-primary mr-5">
                {t("Add Kitchen")}
              </Link>
            </div>
          </ModalBody>
        </Modal>
      )}
      <DataTableBase
        tableTitle={t("Menus")}
        customHeader={
          <div className="w-100">

            {loading ? (
              // Skeleton loading state
              <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
                <div className="w-100" style={{ flex: '0 0 10%' }}>
                  <Skeleton variant="button" />
                </div>
                <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <h6 className="text-nowrap mb-0">{t("Search:")} </h6>
                    <div className="flex-grow-1">
                      <Skeleton variant="input" />
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <h6 className="text-nowrap mb-0">{t("Site:")} </h6>
                    <div className="flex-grow-1">
                      <Skeleton variant="input" />
                    </div>
                  </div>
                </div>

              </div>
            ) : (
              <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
                {canEditMenu && (
                  <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 20%' }}>
                    <Button
                      color={isLimitReached ? "secondary" : "success"}
                      onClick={toggleAddMenuModal}
                      className="d-flex align-items-center justify-content-center w-100"
                      style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                      id="add-menu-btn"
                    >
                      + {t("Add Menu")}
                    </Button>
                    {isLimitReached && (
                      <UncontrolledTooltip target="add-menu-btn">
                        {t("Limit reached. Upgrade to add more.")}
                      </UncontrolledTooltip>
                    )}


                    <Button
                      className="d-flex align-items-center justify-content-center w-100"
                      style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                      color="info"
                      disabled={selectedSiteId === "All"}
                      onClick={() => setShowOrderingModal(true)}
                    >
                      {t("Sort Menus")}
                    </Button>

                    <Button
                      color={isLimitReached ? "secondary" : "primary"}
                      onClick={toggleBulkAddMenusModal}
                      className="d-flex align-items-center justify-content-center w-100"
                      style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                      id="bulk-add-menus-btn"
                    >
                      {t("Bulk Add")}
                    </Button>
                  </div>
                )}
                <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <h6 className="text-nowrap mb-0">{t("Search:")} </h6>
                    <div className="flex-grow-1">
                      <SearchBar
                        submitMode
                        onSearch={searchHandler}
                        placeholder={t("Search menus by name...")}
                      />
                    </div>
                  </div>
                  {sites?.length > 0 && (
                    <div className="d-flex align-items-center gap-2 w-100">
                      <h6 className="text-nowrap mb-0">{t("Select Site:")} </h6>
                      <select
                        className="form-control flex-grow-1"
                        value={selectedSiteId}
                        onChange={handleSiteSelect}
                      >
                        <option key={"all"} value={"All"}>
                          {t("All")}
                        </option>
                        {sites.map((site) => (
                          <option key={site.site_id} value={site.site_id}>
                            {site.site_id} - {site.name}
                          </option>
                        ))}
                      </select>

                    </div>
                  )}
                </div>

              </div>
            )
            }
          </div>
        }
        data={menus}
        columns={columns}
        loading={isLoading}
        paginationTotalRows={totalRows}
        onChangePage={onChangePage}
        // onChangeRowsPerPage={onChangeRowsPerPage}
        onHeaderAddBtnClick={null}
        onHeaderDeleteBtnClick={() => {
          alert(t("Soon"));
        }}
        // onRowEditBtnClick={toggleEditMenuModal}
        // onRowDeleteBtnClick={toggleDeleteMenuModal}
        onSearchIconClick={searchHandler}
        actionColWidth="100px"
        showSearch={false}
        showActionColumn={canEditMenu}
        showSubHeader={false}
        showActionButtons={false}
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-warning"
              onClick={() => {
                toggleEditMenuModal(row);
              }}
              title={t("Edit")}
            >
              <i className="ri-edit-fill"></i>{" "}
            </button>

            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                showConfirm(
                  () => {
                    deleteMenuFun(row.menu_id);
                  },
                  () => {
                    console.log(t("Cancelled"));
                  },
                  t("Confirmation"), // Title of the confirmation dialog
                  `${t("Are you sure you want to delete Menu")} ${row?.name} (id: ${row?.menu_id})?` // Question displayed in the dialog
                );
              }}
              title={t("Delete")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      />

      {showAddMenuModal && !showSiteError && !showKitchenError && (
        <AddMenu
          siteList={sites}
          toggleAddMenuModal={toggleAddMenuModal}
          showAddMenuModal={showAddMenuModal}
        />
      )}

      {selectedRow && (
        <EditMenu
          siteList={sites}
          toggleEditMenuModal={toggleEditMenuModal}
          showEditMenuModal={showEditMenuModal}
          rowData={selectedRow}
        />
      )}

      {showBulkAddMenusModal && !showSiteError && !showKitchenError && (
        <BulkAddMenusModal
          isOpen={showBulkAddMenusModal}
          toggle={toggleBulkAddMenusModal}
          selectedSiteId={selectedSiteId}
          onSuccess={() => {
            fetchUsage();
            getMenus(
              selectedSiteId === "All" ? null : parseInt(selectedSiteId),
              currentPage,
              rowsPerPage,
              searchTerm
            );
          }}
        />
      )}

      {showOrderingModal && selectedSiteId !== "All" && (
        <MenuOrderingModal
          isOpen={showOrderingModal}
          toggle={() => setShowOrderingModal(false)}
          menus={menus}
          siteId={parseInt(selectedSiteId)}
        />
      )}

      <PlanUpgradeModal
        isOpen={showUpgradeModal}
        toggle={toggleUpgradeModal}
      />
    </>
  );
};

export default Menu;
